// Pure parsers for the preview page: no React, no I/O.

export type PreviewKind = "pdf" | "image" | "sheet" | "zip" | "csv" | "edi" | "xml" | "text" | "unsupported";

const IMAGE_EXT = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];

export function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot + 1).toLowerCase();
}

export function kindFor(name: string): PreviewKind {
  const ext = extensionOf(name);
  if (ext === "pdf") return "pdf";
  if (IMAGE_EXT.includes(ext)) return "image";
  if (ext === "xlsx" || ext === "xlsm") return "sheet"; // legacy .xls is not readable by exceljs
  if (ext === "zip") return "zip";
  if (ext === "csv" || ext === "tsv") return "csv";
  if (ext === "edi" || ext === "x12" || ext === "edifact") return "edi";
  if (ext === "xml") return "xml";
  if (ext === "txt" || ext === "log" || ext === "json") return "text";
  return "unsupported";
}

/** RFC 4180-ish CSV/TSV parser: quoted fields, doubled quotes, CRLF inside quotes. */
export function parseDelimited(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i++; } else quoted = false;
      } else field += c;
    } else if (c === '"' && field === "") quoted = true;
    else if (c === delimiter) { row.push(field); field = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && src[i + 1] === "\n") i++;
      row.push(field); rows.push(row); row = []; field = "";
    } else field += c;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  return rows;
}

/** Comma, tab, semicolon or pipe - whichever appears most in the first line. */
export function sniffDelimiter(text: string): string {
  const first = text.split(/\r?\n/, 1)[0] ?? "";
  const counts = [",", "\t", ";", "|"].map((d) => [d, first.split(d).length - 1] as const);
  return counts.sort((a, b) => b[1] - a[1])[0][0];
}

export type EdiSegment = { tag: string; elements: string[]; indent: number };
export type EdiParse = { dialect: "X12" | "EDIFACT" | "unknown"; segments: EdiSegment[] };

// Segments that open a nesting level, and the ones that close it, for indentation only.
const OPEN = new Set(["ISA", "GS", "ST", "UNB", "UNG", "UNH"]);
const CLOSE = new Set(["IEA", "GE", "SE", "UNZ", "UNE", "UNT"]);

/**
 * Splits an interchange into segments. X12 is detected by a leading ISA (fixed 106-char header:
 * element separator is char 4, segment terminator char 106); EDIFACT by UNA/UNB (UNA carries its
 * own separators, otherwise the defaults + ' ? escape). Anything else is split on ~ or newline.
 */
export function parseEdi(raw: string): EdiParse {
  const text = raw.replace(/^﻿/, "").trimStart();
  let elementSep = "*";
  let segmentEnd = "~";
  let escape = "";
  let dialect: EdiParse["dialect"] = "unknown";
  let body = text;

  if (text.startsWith("ISA") && text.length >= 106) {
    dialect = "X12";
    elementSep = text[3];
    segmentEnd = text[105];
  } else if (text.startsWith("UNA") || text.startsWith("UNB")) {
    dialect = "EDIFACT";
    elementSep = "+";
    segmentEnd = "'";
    escape = "?";
    if (text.startsWith("UNA") && text.length >= 9) {
      elementSep = text[4];
      escape = text[6] === " " ? "" : text[6];
      segmentEnd = text[8];
      body = text.slice(9);
    }
  }

  const pieces: string[] = [];
  let current = "";
  for (let i = 0; i < body.length; i++) {
    const c = body[i];
    if (escape && c === escape) { current += c + (body[i + 1] ?? ""); i++; continue; }
    if (c === segmentEnd) { pieces.push(current); current = ""; } else current += c;
  }
  if (current.trim()) pieces.push(current);
  const lines = dialect === "unknown" && pieces.length <= 1 ? text.split(/\r?\n/) : pieces;

  let depth = 0;
  const segments: EdiSegment[] = [];
  for (const piece of lines) {
    const seg = piece.replace(/^[\r\n]+/, "").replace(/[\r\n]+$/, "");
    if (!seg) continue;
    const parts = escape ? splitEscaped(seg, elementSep, escape) : seg.split(elementSep);
    const tag = parts[0].trim();
    if (CLOSE.has(tag)) depth = Math.max(0, depth - 1);
    segments.push({ tag, elements: parts.slice(1), indent: depth });
    if (OPEN.has(tag)) depth++;
  }
  return { dialect, segments };
}

function splitEscaped(seg: string, sep: string, escape: string): string[] {
  const out: string[] = [];
  let cur = "";
  for (let i = 0; i < seg.length; i++) {
    if (seg[i] === escape) { cur += seg[i] + (seg[i + 1] ?? ""); i++; }
    else if (seg[i] === sep) { out.push(cur); cur = ""; }
    else cur += seg[i];
  }
  out.push(cur);
  return out;
}

/** Re-indents XML for reading. Falls back to the original text if it is not well-formed. */
export function prettyXml(xml: string): string {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  if (doc.querySelector("parsererror")) return xml;
  const lines: string[] = [];
  const walk = (node: Node, depth: number) => {
    const pad = "  ".repeat(depth);
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      const attrs = Array.from(el.attributes).map((a) => ` ${a.name}="${a.value}"`).join("");
      const kids = Array.from(el.childNodes).filter((n) => n.nodeType !== Node.TEXT_NODE || n.textContent?.trim());
      if (!kids.length) lines.push(`${pad}<${el.tagName}${attrs}/>`);
      else if (kids.length === 1 && kids[0].nodeType === Node.TEXT_NODE)
        lines.push(`${pad}<${el.tagName}${attrs}>${kids[0].textContent!.trim()}</${el.tagName}>`);
      else {
        lines.push(`${pad}<${el.tagName}${attrs}>`);
        kids.forEach((k) => walk(k, depth + 1));
        lines.push(`${pad}</${el.tagName}>`);
      }
    } else if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) lines.push(pad + node.textContent.trim());
    else if (node.nodeType === Node.COMMENT_NODE) lines.push(`${pad}<!--${node.textContent}-->`);
    else if (node.nodeType === Node.CDATA_SECTION_NODE) lines.push(`${pad}<![CDATA[${node.textContent}]]>`);
  };
  walk(doc.documentElement, 0);
  return lines.join("\n");
}
